// Presentation rules per ISO-4217 code: which symbol, and which side of the
// number it goes on. Anything not listed renders as "CODE 1,234" -- so a
// currency an instance enables that we've never heard of still displays
// correctly rather than silently picking the wrong symbol.
//
// Deliberately NOT Intl's own `style: "currency"`: it is locale-dependent and
// would change what existing users see. In a hu-HU browser it renders EUR as
// "1 234 EUR" and in en-US it renders HUF as "HUF 1,234" -- and it rounds to
// the currency's minor units, dropping the fractional part this app has always
// shown. This map keeps EUR and HUF byte-identical to what they were while
// generalising to every other code.
const CURRENCY_DISPLAY = {
  EUR: { symbol: "€", position: "prefix" },
  HUF: { symbol: "Ft", position: "suffix" },
  USD: { symbol: "$", position: "prefix" },
  GBP: { symbol: "£", position: "prefix" },
  CHF: { symbol: "CHF", position: "prefix" },
  SEK: { symbol: "kr", position: "suffix" },
  NOK: { symbol: "kr", position: "suffix" },
  DKK: { symbol: "kr", position: "suffix" },
  PLN: { symbol: "zł", position: "suffix" },
  CZK: { symbol: "Kč", position: "suffix" },
  RON: { symbol: "lei", position: "suffix" },
};

// The fallback default. The real per-instance default comes from
// branding.default_currency (GET /api/branding) and is passed in by callers
// that have it; this only covers a formatter called with no currency at all.
export const FALLBACK_CURRENCY = "EUR";

export function formatMoney(n, currency = FALLBACK_CURRENCY) {
  if (n == null) return "—";
  const formatted = new Intl.NumberFormat().format(n);
  const display = CURRENCY_DISPLAY[currency];
  if (!display) return `${currency} ${formatted}`;
  return display.position === "suffix"
    ? `${formatted} ${display.symbol}`
    : `${display.symbol}${formatted}`;
}

// Plan 4.2: never sum two currencies into one total -- render each currency
// that actually has a nonzero amount from a {currency: amount} dict, instead
// of picking one arbitrarily. Shared by every page that displays a
// per-currency money breakdown (Dashboard, Utilization).
//
// Iterating Object.entries is what let the currency set become per-instance
// config without an API change: the backend still returns a {code: amount}
// dict, it just has the instance's own keys now.
export function formatMoneyByCurrency(byCurrency, emptyCurrency = FALLBACK_CURRENCY) {
  if (byCurrency == null) return "—";
  const parts = Object.entries(byCurrency).filter(([, v]) => v).map(([c, v]) => formatMoney(v, c));
  return parts.length ? parts.join(" · ") : formatMoney(0, emptyCurrency);
}
